import { Button, Slider } from 'antd'
import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { IconPause, IconPlayfilled } from '@/components/common/Icons'

export const VideoPlayer: FC<{
  src: string
}> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)

  /**
   * 재생 상태, 진행도 등 업데이트
   */
  const handlePlay = useCallback(() => {
    setIsPlaying(true)
  }, [])

  const handlePause = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const handleEnded = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const handleTimeUpdate = useCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => {
      const target = e.target

      if (!(target instanceof HTMLVideoElement)) {
        return
      }

      setDuration(target.duration)
      setCurrentTime(target.currentTime)
    },
    [],
  )

  /**
   * 비디오 재생/일시정지 토글
   */
  const togglePlayVideo = useCallback(() => {
    const videoEl = videoRef.current

    if (!videoEl) {
      return
    }

    if (videoEl.paused || videoEl.ended) {
      videoEl.play()
    } else {
      videoEl.pause()
    }
  }, [])

  /**
   * 비디오 시간 조정
   */
  const handleSeek = useCallback((value: number) => {
    const videoEl = videoRef.current

    if (!videoEl) {
      return
    }

    videoEl.currentTime = value
  }, [])

  /**
   * 재생 프로그레스 바 포인터 다운/업 시 재생 상태를 변경함\
   * 프로그레스 바를 조정할 때는 일시 정지 상태로 되도록 하고\
   * 조정이 끝나면 재생 상태를 원복함
   */
  const nowPlayingRef = useRef(false)

  const handlePointerUp = useCallback(() => {
    const videoEl = videoRef.current

    if (nowPlayingRef.current) {
      videoEl?.play()
    }

    document.removeEventListener('pointerup', handlePointerUp)
  }, [])
  const handlePointerDown = useCallback(() => {
    const videoEl = videoRef.current

    if (!videoEl) {
      return
    }

    nowPlayingRef.current = !videoEl.paused && !videoEl.ended

    if (nowPlayingRef.current) {
      videoEl.pause()
    }

    document.addEventListener('pointerup', handlePointerUp)
  }, [handlePointerUp])

  useEffect(() => {
    return () => document.removeEventListener('pointerup', handlePointerUp)
  }, [handlePointerUp])

  return (
    <VideoPlayerContainer>
      <Video
        src={src}
        playsInline
        ref={videoRef}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        onClick={togglePlayVideo}
        onContextMenu={(e) => e.preventDefault()}
      />
      <Controls>
        <PlayPauseButton type="primary" onClick={togglePlayVideo}>
          {isPlaying ? <IconPause /> : <IconPlayfilled />}
        </PlayPauseButton>
        <ProgressBarContainer onPointerDown={handlePointerDown}>
          <ProgressBar
            min={0}
            max={duration}
            step={0.1}
            value={currentTime}
            onChange={(value) => {
              handleSeek(value)
            }}
            tooltip={{
              open: false,
            }}
          />
        </ProgressBarContainer>
      </Controls>
    </VideoPlayerContainer>
  )
}

const VideoPlayerContainer = styled.div`
  width: 100%;
`

const Video = styled.video`
  width: 100%;
  background-color: #000000;
  margin-bottom: 20px;
  cursor: pointer;
`

const Controls = styled.div`
  display: flex;

  gap: 10px;
  align-items: center;
`

const PlayPauseButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  width: 28px;
  height: 28px;
  padding: 0;

  svg {
    path,
    rect {
      fill: currentColor;
    }
  }
`

const ProgressBarContainer = styled.div`
  flex: 1;
  padding-right: 6px;
`

const ProgressBar = styled(Slider)`
  height: 15px;
  padding-block: 5.5px;

  .ant-slider-rail {
    height: 7px;
    border-radius: 9999px;
  }

  .ant-slider-track {
    height: 7px;
    border-radius: 9999px;
    background-color: #146ef5;
  }

  .ant-slider-handle {
    top: 4px;
  }

  &:hover {
    .ant-slider-track {
      background-color: #146ef5;
    }
  }
`
